import axiosInstance from './axios';
import { apiBaseURL } from 'config/axios';
import Utils from 'services/Utils';
import useSWR from 'swr';
import { Cliente } from './ClientesService';

const entityApiRoute = 'calendar';

export interface CalendarColor {
  id: string;
  background: string;
  foreground: string;
}

export interface CalendarEventPost {
  id?: number;
  cliente_id: number;
  operador_id_1: number;
  operador_id_2?: number;
  operador_id_3?: number;
  operador_id_4?: number;
  operador_id_5?: number;
  servicio_id_1: number;
  servicio_id_2: number;
  servicio_id_3: number;
  servicio_id_4: number;
  startDate: Date;
  endDate: Date;
  recurrence: string[];
  precio: string;
  comprobante: boolean;
  refuerzo: boolean;
  recurrencia: number;
  personalizarFrecuencia: string;
  personalizarRepetirCada: number;
  personalizarFrecuenciaMensual: string;
}

export interface CalendarEventGet {
  id: string;
  colorId: number;
  summary: string;
  start: Date;
  end: Date;
  recurringEventId: string;
  cliente_id: number;
  operadores: number[];
  servicios: number[];
  startDate: Date;
  endDate: Date;
  recurrence: number;
  precio: string;
  comprobante: boolean;
  refuerzo: boolean;
  recurrencia: number;
  personalizarFrecuencia: string;
  personalizarRepetirCada: number;
  personalizarFrecuenciaMensual: string;
  comentario: string;
  estado: string;
}


export interface EventoConsorcio {
  location: string;
  summary: string;
  startDateTime: Date;
  endDateTime: Date;
  momentoDia: String;
  dia: string;
  mes: string;
  nombreDia: string;
  horario: string;
}

export interface ServicioConsorcio {
  id: number;
  key: string;
  description: string;
  precio: number;
  created_at: Date;
  updated_at: Date;
}

export interface AvisoConsorcio {
  evento: EventoConsorcio;
  cliente: Cliente;
  servicio: ServicioConsorcio;
}

export const fetchCalendarNewColors = async (): Promise<CalendarColor[]> => {
  const url = `${apiBaseURL}/${entityApiRoute}/newcolors`;
  const response = await axiosInstance.get(url);
  const colores = response.data.result;
  return colores;
}

const createEntity = async (entity: CalendarEventPost) => {
  const url = `${apiBaseURL}/${entityApiRoute}/entries`;
  await axiosInstance.post(url, entity);
}

const updateEntity = async (id: string, updateData: CalendarEventPost) => {
  const url = `${apiBaseURL}/${entityApiRoute}/entries/${id}`;
  await axiosInstance.put(url, updateData);
}

const updateEntityParcial = async (id: string,
  updateData: { estado?: number, comentario?: string, startDate?: Date, endDate?: Date }
) => {
  const url = `${apiBaseURL}/${entityApiRoute}/entries/${id}`;
  await axiosInstance.patch(url, updateData);
}

const deleteEntity = async (id: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}/entries/${id}`;
  await axiosInstance.delete(url);
}

const fetchOne = async (id: string) => {
  const url = `${apiBaseURL}/${entityApiRoute}/entries/${id}`;
  try {
    const response = await axiosInstance.get(url);
    const entity = response.data?.result.googleEvent;
    return mapResultToCalendarEventGet(entity);
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error("No se encontraron datos con el ID proporcionado");
    }
    throw new Error("Ocurrio un error al obtener el dato. " + error.message);
  }
}

const fetchAll = async (params): Promise<CalendarEventGet[]> => {
  const url = `${apiBaseURL}/${entityApiRoute}/entries`;
  const response = await axiosInstance.get(url, { params: { ...params } });
  const tempResult: any[] = response.data?.result;
  const result = tempResult.map((googleEvent) => {
    const entity = googleEvent.googleEvent;
    return mapResultToCalendarEventGet(entity);
  });
  return result;
}

const fetchAvisosConsorcio = async (params): Promise<AvisoConsorcio[]> => {
  const url = `${apiBaseURL}/${entityApiRoute}/avisosconsorcio`;
  const response = await axiosInstance.get(url, { params: { ...params } });
  const result: AvisoConsorcio[] = response.data?.result;
  return result;
}

export const mapResultToCalendarEventGet = (entity): CalendarEventGet => {
  const sharedExtendedProperties = entity.extendedProperties.shared;
  entity = Utils.removeNullValues(entity);
  const operadores: number[] = [];
  const servicios: number[] = [];
  Object.keys(sharedExtendedProperties).forEach(fieldname => {
    if (fieldname.includes('operador')) {
      const operadorID = fieldname.replace('operador', '');
      operadores.push(Number.parseInt(operadorID));
    }
  });
  Object.keys(sharedExtendedProperties).forEach(fieldname => {
    if (fieldname.includes('servicio')) {
      const servicioID = fieldname.replace('servicio', '');
      servicios.push(Number.parseInt(servicioID));
    }
  });
  return {
    id: entity.id,
    colorId: Number.parseInt(entity.colorId),
    summary: entity.summary,
    start: entity.start.date || entity.start.dateTime,
    end: entity.end.date || entity.end.dateTime,
    recurringEventId: entity.recurringEventId,
    cliente_id: Number.parseInt(sharedExtendedProperties.cliente),
    operadores,
    servicios,
    startDate: new Date(entity.start.dateTime),
    endDate: new Date(entity.end.dateTime),
    recurrence: entity.recurrence,
    precio: sharedExtendedProperties.precio,
    comprobante: sharedExtendedProperties.comprobante === "true" ? true : false,
    refuerzo: sharedExtendedProperties.refuerzo === "true" ? true : false,
    recurrencia: Number(sharedExtendedProperties.recurrencia),
    personalizarFrecuencia: sharedExtendedProperties.personalizarFrecuencia,
    personalizarRepetirCada: Number(sharedExtendedProperties.personalizarRepetirCada),
    personalizarFrecuenciaMensual: sharedExtendedProperties.personalizarFrecuenciaMensual
      ? sharedExtendedProperties.personalizarFrecuenciaMensual : ' ',
    estado: sharedExtendedProperties.estado,
    comentario: sharedExtendedProperties.comentario
  }
}

export const useCalendarColors = () => {
  const url = `${apiBaseURL}/${entityApiRoute}/newcolors`;
  const { data, error, mutate } = useSWR(url, fetchCalendarNewColors);
  return {
    data,
    isLoading: !error && !data,
    isError: error,
    mutate
  }
}

const fetchOperadorSemana = async (operadorId: number, startDateTime: string, endDateTime: string): Promise<CalendarEventGet[]> => {
  const url = `${apiBaseURL}/${entityApiRoute}/operador/${operadorId}/semana`;
  const response = await axiosInstance.get(url, { params: { startDateTime, endDateTime } });
  const tempResult: any = response.data;
  //mapea los servicios a servicioEventGet
  const resultServiciosSemana = tempResult.map(googleEvent => {
    // const entity = googleEvent.googleEvent;
    return mapResultToCalendarEventGet(googleEvent);
  });
  tempResult.serviciosSemana = resultServiciosSemana;
  return resultServiciosSemana;
}

export default {
  createEntity,
  updateEntity,
  updateEntityParcial,
  deleteEntity,
  fetchOne,
  fetchAll,
  fetchCalendarNewColors,
  fetchOperadorSemana,
  fetchAvisosConsorcio
}
